import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import {identity, isNumber, isString} from 'lodash';

/*
 * Types.
 */

type EnumLike = {[key: string]: string | number};
type EnumKeyOf<T extends EnumLike> = string & keyof T;
type EnumValueOf<T extends EnumLike> = T[keyof T];

export class EnumType<T extends EnumLike> extends t.Type<EnumValueOf<T>, string | number, unknown> {
  constructor(enumObject: T) {
    const isEnumValue = makeEnumTypeGuard(enumObject);

    const validate = (value: unknown, context: t.Context): E.Either<t.Errors, EnumValueOf<T>> => {
      // Reuse the type guard, since the runtime and encoded values are the same.
      if (isEnumValue(value)) {
        return t.success(value);
      }

      // For backward compatibility, also decode numbers that were encoded as strings.
      const numericValue = isString(value) ? Number(value) : NaN;
      if (isEnumValue(numericValue)) {
        return t.success(numericValue);
      }

      return t.failure(value, context);
    };

    // The runtime and encoded values are the same.
    const encode = identity;

    super('Enum', isEnumValue, validate, encode);
  }
}

/*
 * Helpers.
 */

/** Returns the keys of an enum. */
export function collectEnumKeys<T extends EnumLike>(enumObject: T): ReadonlyArray<EnumKeyOf<T>> {
  return Object.freeze(
    Object.keys(enumObject)
      // Filter out reverse mappings for numeric members.
      .filter((key): key is EnumKeyOf<T> => {
        const numberKey = Number(key);
        return isNaN(numberKey) || enumObject[enumObject[key]] !== numberKey;
      }),
  );
}

/** Returns the values of an enum. */
export function collectEnumValues<T extends EnumLike>(enumObject: T): ReadonlyArray<EnumValueOf<T>> {
  return Object.freeze(collectEnumKeys(enumObject).map((key) => enumObject[key]));
}

/** Build a type guard for the specified Enum. */
export function makeEnumTypeGuard<T extends EnumLike>(enumObject: T) {
  const enumValues = new Set<string | number>(collectEnumValues(enumObject));

  return (value: unknown): value is EnumValueOf<T> =>
    (isString(value) || isNumber(value)) && enumValues.has(value);
}

/** Build a runtime type for the specified Enum. */
export function makeEnumRuntimeType<T extends EnumLike>(enumObject: T) {
  return new EnumType(enumObject);
}
